import Tags from 'components/Tags/Tags';
import styles from './keywordInput.module.scss';
import i18n from 'i18n';
import { Form } from 'react-bootstrap';
import { compact, partial, trim, uniq } from 'lodash';
import closeSmallIcon from 'assets/close-small.svg';
import { KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import classnames from 'classnames/bind';
import { SelectOptions } from 'components/commonType';
import { withFormikField } from 'hoc/withFormikField';
import { validateEmpty } from 'utils/ValidateUtils';

const cx = classnames.bind(styles);

export type KeywordInputProps = {
  value: string[];
  error?: string | string[];
  inputDisabled?: boolean;
  onChange: (keywords: string[]) => void;
};

export const KeywordInput: React.FC<KeywordInputProps> = ({
  value,
  error,
  inputDisabled = false,
  onChange
}) => {

  const keywordInputRef = useRef<HTMLInputElement>(null);
  const isComposing = useRef<boolean>(false);

  const inputColClassName = useMemo(() => cx(styles.col, {
    hasError: error !== undefined
  }), [error]);

  const renderErrorTip = useCallback((error: string | Array<string>) => {
    if (error) {
      if (Array.isArray(error)) {
        return error.map((err, index) => <div key={index} className={styles.errorTip}>{err}</div>);
      } else {
        return <div className={styles.errorTip}>{error}</div>;
      }
    }
  }, []);

  const addKeyword = useCallback((searchKeywords: string[]): void => {
    onChange(uniq([...value, ...searchKeywords]));
  }, [value, onChange]);

  const handleAddSearchString = useCallback((): void => {
    const inputRef = keywordInputRef.current;
    if (!inputRef) {
      return;
    }
    addKeyword(compact(inputRef.value.split(',').map(trim)));
    inputRef.value = '';
  }, [addKeyword]);

  const onEnterPress = useCallback((e: KeyboardEvent<HTMLInputElement>): void => {
    if (isComposing.current) return; // Prevent triggering during IME composition
    if (e.key.toLowerCase() === 'enter' && e.shiftKey === false) {
      e.preventDefault();
      handleAddSearchString();
    }
  }, [handleAddSearchString]);

  const handleOnInputTagsChange = useCallback((value) => {
    onChange([...value.map((option: SelectOptions) => option.value)]);
  }, [onChange]);

  const handleRemoveAllInputKeywords = useCallback((): void => {
    onChange([]);
  }, [onChange]);

  const handleOnClickCol = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target instanceof HTMLElement && e.target.className !== 'object' && e.target.className !== 'react-tagsinput-tag') {
      keywordInputRef.current?.focus();
    }
  }, []);

  const inputTagsValue = useMemo(() => {
    return value.map(keyword => ({ value: keyword, label: keyword }));
  }, [value]);

  const tagsClassName = useMemo(() => cx(styles.tags), []);

  const handleComposition = useCallback((composition: boolean) => {
    isComposing.current = composition;
  }, []);

  return (
    <>
      <div className={inputColClassName} onClick={handleOnClickCol}>
        {inputTagsValue.length > 0 &&
          <Tags
            className={tagsClassName}
            value={inputTagsValue}
            onChange={handleOnInputTagsChange}
          />
        }
        { !inputDisabled && (
          <Form.Control
            ref={keywordInputRef}
            type='text'
            name='searchKeywords'
            autoComplete='off'
            onKeyDown={onEnterPress}
            onCompositionStart={partial(handleComposition, true)}
            onCompositionEnd={partial(handleComposition, false)}
            placeholder={i18n.t<string>('limitation.placeholders.searchKeywords')}
          />
        )}
        <div className={styles.clearButton} onClick={handleRemoveAllInputKeywords}>
          <img src={closeSmallIcon} alt={'close'}/>
        </div>
      </div>
      {error && renderErrorTip(error)}
    </>
  );
};

export type FormikKeywordInputProps = {
  name: string;
  validate?: (keywords: string[]) => string | undefined;
};

export const FormikKeywordInput = withFormikField<FormikKeywordInputProps>(KeywordInput);

export const renderFormikKeywordInput = (
  name: string,
  validate: (keywords: string[]) => string | undefined = validateEmpty
) => {

  return <FormikKeywordInput name={name} validate={validate}/>;
};
